import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { VendorStatusPill } from '@/components/VendorStatusPill';
import { getProperty } from '@/lib/api';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowLeft, Building2, Users, FileText, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function PropertyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAssignVendor, setShowAssignVendor] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [assigning, setAssigning] = useState(false);
  const { user } = useAuth();
  const isEnterprise = user?.plan_tier === 'enterprise';

  const load = async () => {
    try {
      const { data } = await getProperty(id);
      setProperty(data);
    } catch { }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const handleVendorPaidToggle = async (vendorId, checked) => {
    try {
      await api.put(`/vendors/${vendorId}/vendor-paid`, { vendor_paid: checked });
      toast.success(checked ? 'Vendor set to Vendor-Paid' : 'Vendor set to PM-Paid');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Failed');
    }
  };

  const loadVendors = async () => {
    try {
      const { data } = await api.get('/vendors?limit=1000');
      setVendors(data.items || []);
    } catch { toast.error('Failed to load vendors'); }
  };

  const handleAssignVendor = async () => {
    if (!selectedVendor) {
      toast.error('Please select a vendor');
      return;
    }
    setAssigning(true);
    try {
      await api.post(`/vendors/${selectedVendor}/assign-property`, { property_id: id });
      toast.success('Vendor assigned to property');
      setShowAssignVendor(false);
      setSelectedVendor('');
      load(); // Reload property data
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign vendor');
    } finally {
      setAssigning(false);
    }
  };

  const handleUnassignVendor = async (vendorId) => {
    if (!window.confirm('Unassign this vendor from the property?')) return;
    try {
      await api.delete(`/vendors/${vendorId}/unassign-property/${id}`);
      toast.success('Vendor unassigned from property');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unassign');
    }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-64 rounded-xl" /></div>;
  if (!property) return <p className="text-muted-foreground">Property not found</p>;

  return (
    <div data-testid="property-detail-page" className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/properties')}><ArrowLeft className="h-4 w-4" /></Button>
        <div>
          <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{property.name}</h1>
          <p className="text-sm text-muted-foreground">{property.address}, {property.city} {property.state} {property.zip_code}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[{ label: 'Type', value: property.property_type, icon: Building2 },
          { label: 'Units', value: property.units, icon: Building2 },
          { label: 'Vendors', value: property.vendors?.length || 0, icon: Users }
        ].map((item, i) => (
          <Card key={i} className="rounded-xl">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="p-2 rounded-lg bg-muted"><item.icon className="h-5 w-5 text-muted-foreground" /></div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">{item.label}</p>
                <p className="text-xl font-semibold capitalize" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{item.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Vendors Table */}
      <Card className="rounded-xl">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Assigned Vendors</CardTitle>
          <Button size="sm" onClick={() => { loadVendors(); setShowAssignVendor(true); }}>
            <Plus className="mr-2 h-3 w-3" /> Add Vendor
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {property.vendors?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead>Status</TableHead>
                  {isEnterprise && <TableHead className="text-center">Vendor-Paid</TableHead>}
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {property.vendors.map(v => (
                  <TableRow key={v.id} className="hover:bg-muted/50 transition-colors duration-150">
                    <TableCell className="font-medium text-sm cursor-pointer" onClick={() => navigate(`/vendors/${v.id}`)}>{v.name}</TableCell>
                    <TableCell className="text-sm capitalize cursor-pointer" onClick={() => navigate(`/vendors/${v.id}`)}>{v.vendor_type?.replace('_', ' ')}</TableCell>
                    <TableCell className="text-sm tabular-nums cursor-pointer" style={{ fontFamily: 'Space Grotesk, sans-serif' }} onClick={() => navigate(`/vendors/${v.id}`)}>{v.compliance_score}%</TableCell>
                    <TableCell className="cursor-pointer" onClick={() => navigate(`/vendors/${v.id}`)}><VendorStatusPill status={v.status} /></TableCell>
                    {isEnterprise && (
                      <TableCell className="text-center" onClick={e => e.stopPropagation()}>
                        <Switch checked={v.vendor_paid || false} onCheckedChange={(checked) => handleVendorPaidToggle(v.id, checked)} />
                      </TableCell>
                    )}
                    <TableCell onClick={e => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" onClick={() => handleUnassignVendor(v.id)} data-testid={`unassign-vendor-${v.id}`}>
                        Unassign
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="py-12 text-center"><Users className="mx-auto h-10 w-10 text-muted-foreground/40 mb-3" /><p className="text-muted-foreground text-sm">No vendors assigned</p></div>
          )}
        </CardContent>
      </Card>

      {/* Requirements */}
      <Card className="rounded-xl">
        <CardHeader><CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Document Requirements</CardTitle></CardHeader>
        <CardContent>
          {property.requirements?.length > 0 ? (
            <div className="space-y-2">
              {property.requirements.map((r, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-3">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium uppercase">{r.document_type}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    {r.min_coverage && <Badge variant="secondary" className="text-xs">Min: ${Number(r.min_coverage).toLocaleString()}</Badge>}
                    <Badge variant={r.is_required ? 'default' : 'outline'} className="text-xs">{r.is_required ? 'Required' : 'Optional'}</Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-6">No requirements configured</p>
          )}
        </CardContent>
      </Card>

      {/* Assign Vendor Dialog */}
      <Dialog open={showAssignVendor} onOpenChange={setShowAssignVendor}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Vendor to Property</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Select Vendor</Label>
              <Select value={selectedVendor} onValueChange={setSelectedVendor}>
                <SelectTrigger data-testid="assign-vendor-select">
                  <SelectValue placeholder="Choose a vendor" />
                </SelectTrigger>
                <SelectContent>
                  {vendors.filter(v => !property.vendors?.some(pv => pv.id === v.id)).map(v => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.name} ({v.vendor_type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignVendor(false)}>Cancel</Button>
            <Button onClick={handleAssignVendor} disabled={assigning || !selectedVendor} data-testid="assign-vendor-confirm">
              {assigning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {assigning ? 'Assigning...' : 'Assign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
